import { useState, useEffect, ReactNode } from 'react';
import { useLocation, Link } from 'react-router-dom';

import { IconButtonProps, ListItemButton } from '@mui/material';

import {
  AppBar,
  Drawer,
  List,
  Typography,
  ListItemText,
  ListItemIcon,
  IconButton,
  Toolbar,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';

import NotificationBadge from '../features/notificationDrawer/NotificationBadge';
import NotificationDrawer from '../features/notificationDrawer/NotificationDrawer';
import Footer from '../components/Footer';
import ProfileMenu from '../features/auth/ProfileMenu';
import ThemeSwitcherButton from '../features/themeSwitcher/ThemeSwitcherButton';

import { selectedWallChanged } from '../features/walls/wallsSlice';
import { unsubscribeFromAllAnalyzers } from '../features/spectrasWebSocketServer/spectrasWebSocketServerSlice';
import SearchWidget from '../features/searchWidget/SearchWidget';
import { useAppDispatch } from './hooks';
import { styled } from '@mui/material/styles';
import ConfigurationList from 'features/appDrawer/ConfigurationList';
import ViewsList from 'features/appDrawer/ViewsList';
import AlarmsList from 'features/appDrawer/AlarmsList';
import HistoryList from 'features/appDrawer/HistoryList';
import WeatherList from 'features/appDrawer/WeatherList';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';
import { Box } from '@mui/system';
import LinkMenu from 'features/externalLinks/LinkMenu';
import ConnectionStatusWidget from '../features/systemInfo/ConnectionStatusWidget';

const drawerWidth = 240;

type SelectedTab = 'Configuration' | 'Views' | 'History' | 'Alarms' | 'Weather';

const StyledTabs = styled(
  (props: {
    children?: ReactNode;
    value: SelectedTab;
    onChange: (event: React.SyntheticEvent, value: SelectedTab) => void;
  }) => (
    <Tabs
      {...props}
      TabIndicatorProps={{
        children: <span className='MuiTabs-indicatorSpan' />,
      }}
    />
  )
)({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: 40,
    width: '100%',
    backgroundColor: '#FFFFFF',
  },
  paddingBottom: 10,
});

const StyledTab = styled(
  (props: { label: SelectedTab; value: SelectedTab }) => (
    <Tab disableRipple {...props} />
  )
)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  paddingBottom: 0,
  paddingRight: 0,
  paddingLeft: 0,
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#fff',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)',
  },
  minWidth: 70,
}));

interface StyledMenuButtonProps extends IconButtonProps {
  open?: boolean;
}

const StyledMenuButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuButtonProps>(({ theme, open }) => ({
  position: 'absolute',
  top: 47,
  left: open ? 220 : 41,
  border:
    theme.palette.mode !== 'dark' ? '1px solid #e0e0e0' : '1px solid #3d4752',
  zIndex: 999999999,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#333c46' : '#f5f5f5',
  },
}));

const StyledContent = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuButtonProps>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  height: '100vh',
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  maxWidth: '100vw',
  position: 'relative',

  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    maxWidth: `calc(100% - ${drawerWidth}px)`,
    position: 'relative',
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

interface Props {
  children: ReactNode;
}

const AppDrawer = ({ children }: Props) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { data: modules } = useSystemModules({});

  const [selectedTab, setSelectedTab] = useState<SelectedTab>('Views');
  const [open, setOpen] = useState(true);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);

  useEffect(() => {
    if (
      !location.pathname.includes('/wall') &&
      location.pathname !== '/edit-wall' &&
      !location.pathname.includes('detailed-view') &&
      !location.pathname.includes('alarm-wall-live')
    ) {
      dispatch(unsubscribeFromAllAnalyzers());
      dispatch(selectedWallChanged(-1));
    }
  }, [dispatch, location]);

  const toggleDrawerSize = () => {
    setOpen((state) => !state);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <NotificationDrawer
        open={notificationDrawerOpen}
        handleClose={() => setNotificationDrawerOpen(false)}
      />
      <AppBar position='fixed' sx={{ zIndex: 999 }}>
        <Toolbar sx={{ pl: 13 }}>
          <StyledMenuButton
            onClick={toggleDrawerSize}
            size='small'
            disableFocusRipple
            open={open}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </StyledMenuButton>
          <Grid container alignItems='center' wrap='nowrap'>
            <Grid item style={{ height: 30, width: 30 }}>
              <Link to='/'>
                <img
                  height='30px'
                  width='30px'
                  src={'/favicon.ico'}
                  alt='Spectras Logo'
                />
              </Link>
            </Grid>
            <Grid item style={{ paddingLeft: '1rem' }}>
              <Link to='/' style={{ color: 'inherit', textDecoration: 'none' }}>
                <Typography variant='h6' noWrap>
                  SPECTRAS
                </Typography>
              </Link>
            </Grid>
            <Grid item style={{ paddingLeft: '5rem' }}>
              <StyledTabs
                value={selectedTab}
                onChange={(e, v) => setSelectedTab(v)}
              >
                <StyledTab label='Configuration' value='Configuration' />
                <StyledTab label='Views' value='Views' />
                <StyledTab label='Alarms' value='Alarms' />
                <StyledTab label='History' value='History' />
                {modules?.WEATHER && (
                  <StyledTab label='Weather' value='Weather' />
                )}
              </StyledTabs>
            </Grid>
          </Grid>
          <ConnectionStatusWidget isNav={true} />
          <SearchWidget />
          <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={() => setNotificationDrawerOpen(true)}
            size='large'
          >
            <NotificationBadge />
          </IconButton>
          <LinkMenu />
          <ThemeSwitcherButton />
          <ProfileMenu />
        </Toolbar>
      </AppBar>
      <Drawer
        variant='persistent'
        open={true}
        anchor='left'
        sx={{
          width: open ? drawerWidth : 60,
          flexShrink: 0,
          zIndex: 998,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : 60,
            marginTop: '64px',
            zIndex: 998,
          },
        }}
      >
        <List sx={{ padding: 0, pt: '.8rem' }}>
          {location.pathname.includes('/wall/') && (
            <ListItemButton
              component={Link}
              to={'/edit-wall/' + location.pathname.replace('/wall/', '')}
              key={'Edit Wall'}
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'dark' ? '#D7DCE1' : undefined,
              }}
            >
              <ListItemIcon>
                <EditIcon
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? '#D7DCE1' : undefined,
                  }}
                />
              </ListItemIcon>
              {open && <ListItemText primary={'Edit Wall'} />}
            </ListItemButton>
          )}
          {location.pathname.includes('/weather-wall/') && (
            <ListItemButton
              component={Link}
              to={
                '/edit-weather-wall/' +
                location.pathname.replace('/weather-wall/', '')
              }
              key={'Edit Wall'}
            >
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              {open && <ListItemText primary={'Edit Wall'} />}
            </ListItemButton>
          )}
        </List>
        {selectedTab === 'Configuration' && (
          <ConfigurationList drawerOpen={open} setDrawerOpen={setOpen} />
        )}
        {selectedTab === 'Views' && (
          <ViewsList drawerOpen={open} setDrawerOpen={setOpen} />
        )}
        {selectedTab === 'Alarms' && (
          <AlarmsList drawerOpen={open} setDrawerOpen={setOpen} />
        )}
        {selectedTab === 'History' && <HistoryList drawerOpen={open} />}
        {selectedTab === 'Weather' && (
          <WeatherList drawerOpen={open} setDrawerOpen={setOpen} />
        )}
      </Drawer>
      <StyledContent open={open}>
        <DrawerHeader />
        <Box
          sx={{
            minHeight: 'calc(93vh - 64px)',
            maxHeight: 'calc(93vh - 64px)',
          }}
        >
          {children}
        </Box>
        <Footer drawerOpen={open} />
      </StyledContent>
    </Box>
  );
};

export default AppDrawer;
